// src/AudioSpectrum/effects/fireworks.js
import { getHSLColor } from '../colorUtils.js';

export const drawFireworks = (ctx, frequencyData, width, height, fireworksRef) => {
  // 배경을 더 천천히 어둡게 (궤적이 더 오래 남도록)
  ctx.fillStyle = 'rgba(0, 0, 0, 0.03)';
  ctx.fillRect(0, 0, width, height);
  
  const time = Date.now();
  
  // 새로운 불꽃 생성
  for (let i = 1; i < frequencyData.length; i += 10) {
    const value = frequencyData[i];
    if (value > 120 && Math.random() < 0.1) {
      const baseX = (i / frequencyData.length) * width * 10;
      const targetX = (i / frequencyData.length) * width * 10;
      const firework = {
        x: baseX,
        y: height - Math.random() * 30,
        targetX: targetX,
        targetY: Math.random() * height * 0.5 + height * 0.1,
        phase: 'rising',
        life: 1.0,
        color: getHSLColor(Math.random() * 360, 100, 60),
        particles: [],
        intensity: value / 255,
        birthTime: time,
        trail: []
      };
      
      // 바로 폭발하는 불꽃도 일부 생성
      if (Math.random() < 0.3) {
        firework.phase = 'exploding';
        
        const particleCount = Math.floor(firework.intensity * 35 + 15);
        const rings = 1;
        
        for (let ring = 0; ring < rings; ring++) {
          const ringParticles = Math.floor(particleCount / rings);
          
          for (let p = 0; p < ringParticles; p++) {
            const angle = (p / ringParticles) * Math.PI * 2 + Math.random() * 0.3;
            const speed = (Math.random() * 2 + 1.5) * (ring + 1);
            
            firework.particles.push({
              x: firework.targetX,
              y: firework.targetY,
              vx: Math.cos(angle) * speed,
              vy: Math.sin(angle) * speed,
              life: 1.0,
              decay: 0.004 + Math.random() * 0.008,
              size: Math.random() * 1 + 0.5 + ring * 0.5,
              trail: [],
              color: getHSLColor(
                (Math.random() * 60 - 30 + parseInt(firework.color.match(/\d+/)[0])) % 360,
                90 + Math.random() * 10,
                50 + Math.random() * 30
              ),
              sparkles: [],
              ring: ring
            });
          }
        }
      }
      
      const particleCount = Math.floor(firework.intensity * 15 + 10);
      const rings = 3;
      
      for (let ring = 0; ring < rings; ring++) {
        const ringParticles = Math.floor(particleCount / rings);
        
        for (let p = 0; p < ringParticles; p++) {
          const angle = (p / ringParticles) * Math.PI * 2 + Math.random() * 0.3;
          const speed = (Math.random() * 2 + 1.5) * (ring + 1);
          
          firework.particles.push({
            x: firework.targetX,
            y: firework.targetY,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            life: 1.0,
            decay: 0.004 + Math.random() * 0.008,
            size: Math.random() * 3 + 1 + ring * 0.5,
            trail: [],
            color: getHSLColor(
              (Math.random() * 60 - 30 + parseInt(firework.color.match(/\d+/)[0])) % 360,
              90 + Math.random() * 10,
              50 + Math.random() * 30
            ),
            sparkles: [],
            ring: ring
          });
        }
      }
      fireworksRef.current.push(firework);
    }
  }
  
  // 불꽃 업데이트 및 렌더링
  fireworksRef.current = fireworksRef.current.filter(firework => {
    const age = (time - firework.birthTime) / 1000;
    
    if (firework.phase === 'rising') {
      const progress = Math.min(age / 2, 1);
      firework.x = firework.x + (firework.targetX - firework.x) * progress;
      firework.y = firework.y + (firework.targetY - firework.y) * progress;
      
      if (!firework.trail) firework.trail = [];
      firework.trail.push({ x: firework.x, y: firework.y, life: 1.0 });
      if (firework.trail.length > 20) firework.trail.shift();
      
      // 궤적 그리기
      firework.trail.forEach((point, index) => {
        point.life -= 0.005;
        if (point.life > 0) {
          const alpha = point.life * (index / firework.trail.length) * 1.0;
          ctx.fillStyle = firework.color.replace('hsl(', 'hsla(').replace(')', `, ${alpha})`);
          const size = 1 + (index / firework.trail.length) * 1;
          
          ctx.shadowColor = firework.color;
          ctx.shadowBlur = size * 2;
          ctx.beginPath();
          ctx.arc(point.x, point.y, size, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
          
          if (Math.random() < 0.3) {
            ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 0.7})`;
            ctx.beginPath();
            ctx.arc(point.x, point.y, size * 0.5, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      });
      
      ctx.fillStyle = firework.color;
      ctx.shadowColor = firework.color;
      ctx.shadowBlur = 8;
      ctx.beginPath();
      ctx.arc(firework.x, firework.y, 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
      
      if (progress >= 0.95) {
        firework.phase = 'exploding';
        
        const particleCount = Math.floor(firework.intensity * 35 + 15);
        const layers = 2;
        
        for (let layer = 0; layer < layers; layer++) {
          const layerParticles = Math.floor(particleCount / layers);
          const layerDelay = layer * 100;
          
          for (let i = 0; i < layerParticles; i++) {
            const angle = (i / layerParticles) * Math.PI * 2 + (layer * Math.PI / layers);
            const baseSpeed = 1.5 + layer * 0.8;
            const speed = baseSpeed + Math.random() * 1.5;
            
            const particleType = Math.random();
            let size, color, decay;
            
            if (particleType < 0.3) {
              size = 2 + Math.random() * 1;
              color = firework.color;
              decay = 0.003 + Math.random() * 0.005;
            } else if (particleType < 0.7) {
              size = 1 + Math.random() * 0.5;
              color = getHSLColor(
                (parseInt(firework.color.match(/\d+/)[0]) + Math.random() * 60 - 30) % 360,
                90, 75
              );
              decay = 0.005 + Math.random() * 0.008;
            } else {
              size = 0.5 + Math.random() * 0.3;
              color = `hsl(${Math.random() * 60 + 30}, 100%, 90%)`;
              decay = 0.008 + Math.random() * 0.012;
            }
            
            firework.particles.push({
              x: firework.targetX,
              y: firework.targetY,
              vx: Math.cos(angle) * speed,
              vy: Math.sin(angle) * speed,
              life: 1.0,
              decay: decay,
              size: size,
              color: color,
              trail: [],
              type: particleType < 0.3 ? 'bright' : particleType < 0.7 ? 'normal' : 'sparkle',
              birthTime: time + layerDelay,
              layer: layer
            });
          }
        }
      }
    } else if (firework.phase === 'exploding') {
      firework.particles = firework.particles.filter(particle => {
        particle.trail.push({ x: particle.x, y: particle.y, life: 0.8 });
        if (particle.trail.length > 8) particle.trail.shift();
        
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.vy += 0.05;
        particle.vx *= 0.99;
        particle.life -= particle.decay;
        
        if (particle.life > 0) {
          particle.trail.forEach((point, index) => {
            point.life -= 0.12;
            if (point.life > 0) {
              const alpha = particle.life * point.life * (index / particle.trail.length);
              ctx.globalAlpha = alpha;
              ctx.fillStyle = particle.color;
              const trailSize = particle.size * (index / particle.trail.length) * 0.7;
              ctx.beginPath();
              ctx.arc(point.x, point.y, trailSize, 0, Math.PI * 2);
              ctx.fill();
            }
          });
          
          ctx.globalAlpha = particle.life;
          
          if (particle.type === 'bright') {
            ctx.shadowColor = particle.color;
            ctx.shadowBlur = particle.size * 2;
            ctx.fillStyle = particle.color;
            ctx.beginPath();
            ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
            ctx.fill();
            ctx.shadowBlur = 0;
          } else if (particle.type === 'sparkle') {
            ctx.fillStyle = particle.color;
            ctx.strokeStyle = particle.color;
            ctx.lineWidth = particle.size * 0.3;
            
            ctx.beginPath();
            ctx.moveTo(particle.x - particle.size, particle.y);
            ctx.lineTo(particle.x + particle.size, particle.y);
            ctx.moveTo(particle.x, particle.y - particle.size);
            ctx.lineTo(particle.x, particle.y + particle.size);
            ctx.stroke();
            
            ctx.beginPath();
            ctx.arc(particle.x, particle.y, particle.size * 0.4, 0, Math.PI * 2);
            ctx.fill();
          } else {
            ctx.fillStyle = particle.color;
            ctx.beginPath();
            ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
            ctx.fill();
          }
          
          if (Math.random() < 0.3) {
            ctx.strokeStyle = particle.color;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(particle.x - particle.size, particle.y);
            ctx.lineTo(particle.x + particle.size, particle.y);
            ctx.moveTo(particle.x, particle.y - particle.size);
            ctx.lineTo(particle.x, particle.y + particle.size);
            ctx.stroke();
          }
          
          return true;
        }
        return false;
      });
      
      if (firework.particles.length === 0) {
        return false;
      }
    }
    
    ctx.globalAlpha = 1.0;
    return true;
  });
};